<script setup>
import { createReusableTemplate } from '@vueuse/core'
import { isEmpty } from 'lodash'
import { Navigation, Virtual } from 'swiper/modules'
import { Swiper, SwiperSlide } from 'swiper/vue'
import { computed, onMounted, ref } from 'vue'
import { useRoute } from 'vue-router'

import { ROUTE_NAMES } from '@/routes/route-helpers'
import { tracker } from '@/tracking/amplitude'
import { ELEMENT_TYPES_FOR_TRACKING, EVENT_SOURCES } from '@/tracking/amplitude-helpers'
import {
  CHECKIN_PREVIEW_NAVIGATION_BUTTONS,
  CHECKINS_PREVIEW_LIMIT
} from '@/utils/check-ins/check-ins'
import { CHECKIN_ENTITY_KEYS } from '@/utils/entity-keys'
import { isKR } from '@/utils/objective-types'
import { OKR_ELEMENT_KEYS } from '@/utils/view-row-actions'

import CheckinPreviewItem from '@/components/objectives/element-check-in/CheckinPreviewItem'
import CheckinPreviewLoader from '@/components/objectives/element-check-in/CheckinPreviewLoader'

// eslint-disable-next-line import/extensions
import 'swiper/css'
// eslint-disable-next-line import/extensions
import 'swiper/css/navigation'

defineOptions({
  name: 'CheckinPreview'
})

const props = defineProps({
  okrElement: {
    type: Object,
    required: true
  },

  loading: {
    type: Boolean
  },

  data: {
    type: Array,
    required: true
  },

  initialSlideIndex: {
    type: Number,
    default: 0
  },

  showReplies: {
    type: Boolean
  },

  showActions: {
    type: Boolean
  },

  tableActionsAppendTo: {
    type: String,
    default: ''
  },

  trackingSource: {
    type: String,
    default: ''
  }
})

const emit = defineEmits({
  'load-more': null,
  'delete-checkin': null,
  'edit-checkin': null,
  'update-replies': null,
  'update-reply': null,
  init: null,
  'update-reactions': null
})

const indexWhenLoadMore = computed(() => {
  const loadMoreOffset = Math.floor(CHECKINS_PREVIEW_LIMIT / 2)

  return props.data.length - loadMoreOffset
})

const onNavigationNext = ({ activeIndex }) => {
  const isNeedLoadMore = activeIndex === indexWhenLoadMore.value

  if (isNeedLoadMore) {
    // This is where you would typically trigger a load more action
    // If your position is half off offset to the end of the list
    emit('load-more')
  }
}

const [DefineTemplate, ReuseTemplate] = createReusableTemplate()

const fakeLoading = ref(false)

const fakeReload = async (delay = 0) => {
  // This is a workaround to force swiper to update its virtual slides
  // because the swiper has a bug with combined virtual and initialSlide props
  // DO NOT USE :initial-slide="initialSlideIndex" PROPS

  fakeLoading.value = true

  return new Promise(resolve => {
    setTimeout(() => {
      fakeLoading.value = false
      resolve()
    }, delay)
  })
}

const onSliderInit = async (swiper = null) => {
  if (!props.initialSlideIndex) {
    return
  }

  const timeout = swiper ? 300 : 0

  await fakeReload(timeout)

  if (swiper) {
    swiper.slideTo(props.initialSlideIndex, timeout * 2, false)

    if (props.initialSlideIndex >= indexWhenLoadMore.value) {
      emit('load-more')
    }
  }
  emit('init')
}

const swiperRef = ref(null)

const setSwiper = swiper => {
  swiperRef.value = swiper
}

const onPreviewResize = (isActiveSlideResized = false) => {
  if (isActiveSlideResized && swiperRef.value) {
    swiperRef.value.updateAutoHeight(0)
  }
}

const elementTypeId = computed(() => {
  return props.okrElement[OKR_ELEMENT_KEYS.TYPE_ID] || null
})

const existContributeChildren = computed(() => {
  return props.okrElement[OKR_ELEMENT_KEYS.EXIST_CONTRIBUTE_CHILDREN] || false
})

const isMetric = computed(() => isKR(props.okrElement))

const route = useRoute()

const logCheckinViewEvent = () => {
  let source = 'unknown'

  // route can be undefined in confluence macro environment
  if (route && route.name) {
    const sourceMapping = {
      [ROUTE_NAMES.OKR_ELEMENTS_HIERARCHY_TABLE]: EVENT_SOURCES.ALIGNMENT,
      [ROUTE_NAMES.OKR_ELEMENTS_EXPLORER]: EVENT_SOURCES.EXPLORER,
      [ROUTE_NAMES.OKR_ELEMENTS_ROADMAP]: EVENT_SOURCES.ROADMAP,
      [ROUTE_NAMES.HOME_PAGE]: EVENT_SOURCES.HOMEPAGE,
      [ROUTE_NAMES.CUSTOM_DASHBOARD_ITEM]: EVENT_SOURCES.CUSTOM_DASHBOARD
    }

    source = props.trackingSource || (sourceMapping[route.name] ?? source)
  }

  tracker.logEvent('check-in viewed', {
    element_type: isMetric.value
      ? ELEMENT_TYPES_FOR_TRACKING.METRIC
      : ELEMENT_TYPES_FOR_TRACKING.OBJECTIVE,
    element_id: props.okrElement[OKR_ELEMENT_KEYS.ID],
    source
  })
}

onMounted(() => {
  logCheckinViewEvent()
})
</script>

<template>
  <DefineTemplate v-slot="{ item, index }">
    <CheckinPreviewItem
      :item="item"
      :okr-element
      :show-actions
      :show-replies
      :table-actions-append-to
      show-reactions
      @update-reactions="emit('update-reactions', $event)"
      @delete-checkin="emit('delete-checkin', $event)"
      @edit-checkin="emit('edit-checkin', $event)"
      @update-replies="
        emit('update-replies', {
          item,
          replies: $event,
          itemIndex: index
        })
      "
    />
  </DefineTemplate>

  <CheckinPreviewLoader
    v-if="loading && isEmpty(data)"
    :element-type-id
    :exist-contribute-children
  />

  <div v-if="!isEmpty(data)" class="cp-PreviewWrapper">
    <ReuseTemplate v-if="data.length === 1" :index="0" :item="data[0]" @init="onSliderInit" />

    <template v-else>
      <swiper
        :allow-touch-move="false"
        :class="{
          'cp-PreviewSwiper-metric': isMetric,
          'cp-PreviewSwiper-has-contributors': existContributeChildren
        }"
        :modules="[Navigation, Virtual]"
        :navigation="CHECKIN_PREVIEW_NAVIGATION_BUTTONS"
        :virtual="true"
        auto-height
        centered-slides
        class="cp-PreviewSwiper"
        dir="rtl"
        slides-per-view="1"
        @swiper="setSwiper"
        @after-init="onSliderInit"
        @navigation-next="onNavigationNext"
      >
        <swiper-slide
          v-for="(item, index) in data"
          :key="item[CHECKIN_ENTITY_KEYS.ID]"
          v-slot="{ isActive }"
          :virtual-index="index"
        >
          <CheckinPreviewLoader v-if="fakeLoading" :element-type-id :exist-contribute-children />
          <ReuseTemplate
            v-else
            :index
            :is-visible-on-screen="isActive"
            :item
            watch-item-visibility
            @preview-resize="onPreviewResize(isActive)"
          />
        </swiper-slide>
      </swiper>
    </template>
  </div>
</template>

<style lang="scss" scoped>
.cp-PreviewWrapper {
  width: calc(var(--checkin-field-width) + var(--fields-padding-left));
}

.cp-PreviewSwiper {
  @function calculate-min-height($lines) {
    $field-height: 56px;
    $field-gap: 4px;
    $author-height: 24px;
    $author-gap: 12px;
    $reactions-height: 28px;
    $reactions-margin-top: 4px;

    $total-field-gap: ($lines - 1) * $field-gap;

    @return $lines * $field-height + $total-field-gap + $author-height + $author-gap +
      $reactions-height + $reactions-margin-top + $field-gap;
  }

  &:not(&-metric) {
    min-height: calculate-min-height(2);
  }

  &-metric {
    min-height: calculate-min-height(3);
  }

  &-has-contributors {
    min-height: calculate-min-height(2);
  }
}
</style>
